/**
 * Debug-only HTTP logging for the web app. Matches backend/mobile `[HTTP]` format.
 * Never logs Authorization headers or tokens.
 */

const TAG = 'HTTP'

function isDev(): boolean {
  return import.meta.env.DEV
}

function isVerbose(): boolean {
  return import.meta.env.VITE_API_VERBOSE_LOGS === 'true'
}

export function logHttpRequest(method: string, path: string): void {
  if (!isDev()) return
  console.info(`[${TAG}] → ${method} ${path}`)
}

export function logHttpResponse(
  method: string,
  path: string,
  statusCode: number,
  durationMs: number,
  options?: { requestId?: string | null; body?: unknown }
): void {
  if (!isDev()) return

  if (statusCode === 304 || statusCode === 204) {
    console.debug(
      `[${TAG}] ← ${method} ${path} ${statusCode} ${durationMs}ms`,
    )
    return
  }

  const requestId = options?.requestId
  const idPart = requestId ? ` req=${requestId}` : ''
  const summary = summarizeBody(options?.body)
  const summaryPart =
    isVerbose() && summary != null
      ? ` ${JSON.stringify(summary)}`
      : summary != null
        ? ` ${JSON.stringify(summary)}`
        : ''

  const level = statusCode >= 500 ? 'error' : statusCode >= 400 ? 'warn' : 'info'
  const message = `[${TAG}] ← ${method} ${path} ${statusCode} ${durationMs}ms${idPart}${summaryPart}`

  if (level === 'error') console.error(message)
  else if (level === 'warn') console.warn(message)
  else console.info(message)

  if (isVerbose() && options?.body != null) {
    console.debug(`[${TAG}] body:`, options.body)
  }
}

export function logHttpError(
  method: string,
  path: string,
  statusCode: number | null,
  durationMs: number,
  message: string,
  requestId?: string | null
): void {
  if (!isDev()) return
  const idPart = requestId ? ` req=${requestId}` : ''
  const status = statusCode ?? '—'
  console.warn(
    `[${TAG}] ✗ ${method} ${path} ${status} ${durationMs}ms${idPart} — ${message}`,
  )
}

export function requestIdFrom(response: Response): string | null {
  return response.headers.get('x-request-id')
}

export function summarizeBody(data: unknown): Record<string, unknown> | string | null {
  if (data == null) return null
  if (Array.isArray(data)) return { items: data.length }
  if (typeof data !== 'object') return String(typeof data)

  const record = data as Record<string, unknown>
  if ('success' in record && 'data' in record) {
    const inner = record.data
    if (Array.isArray(inner)) return { success: true, data: `[${inner.length}]` }
    if (inner && typeof inner === 'object') {
      return { success: true, data: { keys: Object.keys(inner as object).length } }
    }
    return { success: true, data: typeof inner }
  }
  return { keys: Object.keys(record).length }
}

/** One-line dev startup banner (browser console). */
export function logAppReady(): void {
  if (!isDev()) return
  const api =
    import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api/v1'
  console.info('[web] SitRepp.io frontend ready', { api })
}
